import { useRef } from 'react';

import { useQuery } from '@tanstack/react-query';

import { tokens as ticksTokens } from '@pkg/components/FloorGameTicks/tokens';
import { convertEthToWei, convertWeiToEth, DEFAULT_ETH_MAX_DECIMALS } from '@pkg/components/Sections/Groups/helpers';
import { useSlugFloorPrice } from '@pkg/hooks';
import { FloorGameActivity } from '@pkg/types/floorGame';
import {
    convertIsoDateToDate,
    convertIsoDateToTimestamp,
    DateModifiers,
    getCurrentDate,
    normalizeAddress,
    subtractFromDate,
} from '@pkg/utils';
import { determineTier, getTierMinValue, nextTierValue } from '@pkg/utils/floorGame';

import { Collection } from '@app/client/resources/collections';
import { type CollectionListingsItem } from '@app/client/resources/collections/types';

const FLOOR_GAME_HOT_MIN_SALES = 5;
const FLOOR_GAME_HOT_MAX_SECONDS = 10;

interface FloorGameData {
    slug?: string;
    floorPriceEth?: number;
    floorPriceWei?: number;
    listings?: {
        count: number;
        items: CollectionListingsItem[];
    };
    sales?: {
        count?: number;
    };
    activity?: {
        type: FloorGameActivity;
        lastSale: Date | null;
    };
    isDead?: boolean;
    isLoadingCollectionFloor?: boolean;
    isLoadingContractActiveListings?: boolean;
    isLoadingContractActiveListingsCount?: boolean;
    isLoadingContractSalesByPriceRangeCount?: boolean;
    isLoadingContractSalesByDateCount?: boolean;
    isLoadingContractSalesByPriceAndDate?: boolean;
}

/**
 * React Hook using React Query to fetch all data related to Floor Game.
 */
type UseGetFloorGameDataProps = {
    contractAddress: string;
    slug: string;
    floorPriceWei?: number;
    ticksCount?: number;
    disabled?: boolean;
};
export const useGetFloorGameData = ({
    contractAddress,
    slug = '',
    floorPriceWei = 0,
    ticksCount = ticksTokens.meterSize.normal,
    disabled,
}: UseGetFloorGameDataProps) => {
    const normalizedContractAddress = normalizeAddress(contractAddress);
    const floorGameData: FloorGameData = {};

    // Fetch collection slug and floor price
    const { data: contractFloorPrice, isLoading: isLoadingCollectionFloor } = useSlugFloorPrice(slug, disabled);

    floorGameData.slug = contractFloorPrice?.slug || slug;
    floorGameData.floorPriceWei = contractFloorPrice?.floor_price_wei || floorPriceWei;

    // Get tier ETH value ranges
    const floorPriceEth = convertWeiToEth(floorGameData.floorPriceWei || 0, DEFAULT_ETH_MAX_DECIMALS);
    const currentTier = determineTier(floorPriceEth);
    const currentTierMinPrice = getTierMinValue(currentTier);
    const currentTierMaxPrice = nextTierValue(currentTier);
    floorGameData.floorPriceEth = floorPriceEth;

    // Convert floor price ranges from ETH to WEI
    const minPriceWei = convertEthToWei(currentTierMinPrice);
    const maxPriceWei = convertEthToWei(currentTierMaxPrice) - 1;

    const listingsEnabled = !disabled && Boolean(!isLoadingCollectionFloor || floorPriceWei);
    // Fetch listings for this collection within this tier.
    const { data: contractActiveListings, isLoading: isLoadingContractActiveListings } = useQuery(
        ['contractFloorGameListings', normalizedContractAddress, minPriceWei, maxPriceWei],
        ({ signal }) =>
            Collection.callGetCollectionListings({
                contractAddress: normalizedContractAddress,
                minPriceWei,
                maxPriceWei,
                limit: ticksCount,
                settings: {
                    signal,
                },
            }),
        {
            retry: true,
            enabled: listingsEnabled,
            keepPreviousData: true,
        },
    );
    floorGameData.listings = {
        items: contractActiveListings || [],
        count: (contractActiveListings || []).length,
    };

    // Fetch total number of tokens in a collection for a
    // given tier (by price range)
    const { data: contractActiveListingsCount, isLoading: isLoadingContractActiveListingsCount } = useQuery(
        ['contractFloorGameListingsCount', normalizedContractAddress, minPriceWei, maxPriceWei],
        ({ signal }) =>
            Collection.callCountCollectionListings({
                contractAddress: normalizedContractAddress,
                minPriceWei,
                maxPriceWei,
                settings: {
                    signal,
                },
            }),
        {
            retry: true,
            keepPreviousData: true,
            // We do not need to get total number of listings
            // if previous result was less than 12
            enabled: listingsEnabled,
        },
    );
    if (contractActiveListingsCount) {
        floorGameData.listings.count = contractActiveListingsCount;
    }

    // Fetch total number of sales within current tier
    const { data: contractSalesByPriceRangeCount, isLoading: isLoadingContractSalesByPriceRangeCount } = useQuery(
        ['contractFloorGameTierSalesCount', normalizedContractAddress, minPriceWei, maxPriceWei],
        ({ signal }) =>
            Collection.callCountCollectionSalesByPriceRange({
                contractAddress: normalizedContractAddress,
                minPriceWei,
                maxPriceWei,
                settings: {
                    signal,
                },
            }),
        {
            enabled: listingsEnabled,
            retry: true,
            keepPreviousData: true,
        },
    );
    floorGameData.sales = {
        count: contractSalesByPriceRangeCount || 0,
    };

    const dateNow = useRef(getCurrentDate());

    // Fetch total number of sales in the past 7 days for Death Game
    const dateFromDeathGame = subtractFromDate(dateNow.current, 7, DateModifiers.Days);
    const { data: contractSalesByDateCount, isLoading: isLoadingContractSalesByDateCount } = useQuery(
        ['contractFloorGameSalesCount', normalizedContractAddress, dateFromDeathGame],
        ({ signal }) =>
            Collection.callCountCollectionSalesByDate({
                contractAddress: normalizedContractAddress,
                dateFrom: dateFromDeathGame,
                settings: {
                    signal,
                },
            }),
        {
            enabled: !disabled,
            retry: true,
            keepPreviousData: true,
        },
    );
    floorGameData.isDead = !contractSalesByDateCount;

    // Fetch sales dates from the past 61 minutes (set collection as hot)
    // TODO: Change DateModifiers.Days to DateModifiers.Minutes
    const dateFrom = subtractFromDate(dateNow.current, 61, DateModifiers.Days);
    const { data: contractSalesByPriceAndDate, isLoading: isLoadingContractSalesByPriceAndDate } = useQuery(
        ['contractFloorGameSales', normalizedContractAddress, floorGameData.floorPriceWei],
        ({ signal }) =>
            Collection.callGetCollectionByPriceAndDate({
                contractAddress: normalizedContractAddress,
                floorPriceWei: floorGameData.floorPriceWei || 0,
                dateFrom,
                settings: {
                    signal,
                },
            }),
        {
            enabled: listingsEnabled,
            retry: true,
            keepPreviousData: true,
        },
    );

    // Process last sales date to identify 'hot' collections
    // Criteria: 5 sales within 10 seconds of each other over a 24-hour period
    // A collection is considered 'hot' for 1 hour after criteria is matched
    if (contractSalesByPriceAndDate) {
        let streakLastSale: string | null = null;
        let streakCount = 0;
        for (let i = 0; i < contractSalesByPriceAndDate.length - 1; i++) {
            // Get current sales date being processed and compare it with next one
            const thisSale = contractSalesByPriceAndDate[i].block_timestamp;
            const previousSale = contractSalesByPriceAndDate[i + 1].block_timestamp;

            const thisSaleTimestamp = convertIsoDateToTimestamp(thisSale);
            const previousSaleTimestamp = convertIsoDateToTimestamp(previousSale);

            const timeDiff = thisSaleTimestamp - previousSaleTimestamp;
            const isStreak = timeDiff <= FLOOR_GAME_HOT_MAX_SECONDS * 1000;

            if (!isStreak) {
                // Reset references if not a streak
                streakLastSale = null;
                streakCount = 0;
            } else {
                if (!streakLastSale) {
                    streakLastSale = thisSale;
                    streakCount = 2;
                } else {
                    streakCount++;
                }
            }

            // Enough information to flag this as 'hot' collection
            if (streakCount === FLOOR_GAME_HOT_MIN_SALES && streakLastSale) {
                floorGameData.activity = {
                    type: FloorGameActivity.OnFire,
                    lastSale: convertIsoDateToDate(streakLastSale),
                };
                break;
            }
        }
    }
    return {
        ...floorGameData,
        isLoadingCollectionFloor,
        isLoadingContractActiveListings,
        isLoadingContractActiveListingsCount,
        isLoadingContractSalesByPriceRangeCount,
        isLoadingContractSalesByDateCount,
        isLoadingContractSalesByPriceAndDate,
    };
};
